var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from 'axios';
import { AuthenticationMethodType, AuthenticationResult, AuthenticationStatus } from '@mc-alberta/types';
import { State } from './state';
import { getCardToken, isFatalStatus, getLookupKpiMeta } from './utils';
import { AuthenticationStrategy } from '../types/enums/AuthenticationStrategy';
import { DEFAULT_MANAGED_AUTH_METHODS, STRATEGIES } from '../constants';
import { KpiOperation } from '../types/enums/KpiOperation';
export class Stark extends State {
    constructor() {
        super(...arguments);
        /**
         * Boostraps the initial SDK state.
         *
         * This will fetch/cache applicable authentication methods.
         */
        this.bootstrapStrategies = (supportedMethods) => __awaiter(this, void 0, void 0, function* () {
            if (supportedMethods.includes(AuthenticationMethodType.MANAGED_AUTHENTICATION)) {
                supportedMethods = DEFAULT_MANAGED_AUTH_METHODS;
            }
            if (!supportedMethods.length)
                return; // no usable strategies
            const authenticationMethods = yield this.lookup(supportedMethods);
            const adapters = Object.values(this.adapters);
            yield Promise.all(adapters.map(adapter => adapter.bootstrap(authenticationMethods)));
            this.strategies = STRATEGIES
                .filter(({ strategy }) => { var _a; return (_a = this.getAdapter(strategy)) === null || _a === void 0 ? void 0 : _a.isUsable; })
                .map(({ strategy }) => strategy);
            this.clearSession();
        });
        /**
         * Fetches available authentication methods from authentication provider.
         */
        this.lookup = (supportedMethods) => __awaiter(this, void 0, void 0, function* () {
            try {
                this.kpi.start(KpiOperation.LOOKUP_AUTHENTICATION_METHODS);
                const authenticationContext = Object.assign(Object.assign({}, this.authenticationContext), { supportedMethods });
                if (supportedMethods.includes(AuthenticationMethodType.FIDO2)) {
                    // FIDO specifically requires a device recognition check prior to lookup
                    authenticationContext.consumerDeviceData = yield this
                        .getAdapter(AuthenticationStrategy.FIDO2)
                        .getConsumerDeviceData();
                }
                const { data: { authenticationMethods } } = yield axios.post('/api/accountholder/authentications/lookup', {
                    accountReference: this.accountReference,
                    srcCorrelationId: this.srcCorrelationId,
                    srcClientId: this.srcClientId,
                    srciTransactionId: this.srciTransactionId,
                    serviceId: this.serviceId,
                    authenticationContext
                }, {
                    headers: {
                        'x-src-cx-flow-id': this.traceId
                    }
                });
                this.kpi.end('COMPLETED', getLookupKpiMeta(authenticationMethods));
                return authenticationMethods.filter((authenticationMethod) => supportedMethods.includes(authenticationMethod.authenticationMethodType));
            }
            catch (error) {
                this.kpi.queueError(error);
                this.kpi.end('FAILED');
                // TODO: log error
                return [];
            }
        });
        /**
         * Initializes an authentication session.
         *
         * If no specific strategy is provided, this will cycle through all
         * available methods until one initializes successfully.
         *
         * @param strategy - optional, specific strategy to use
         */
        this.initializeSession = (strategy) => __awaiter(this, void 0, void 0, function* () {
            if (strategy) {
                this.strategy = strategy;
            }
            if (!this.getAdapter(this.strategy)) {
                return this.getSession();
            }
            const result = yield this.invokeAdapterMethod('initializeSession');
            if (result.authenticationStatus === AuthenticationStatus.NOT_SUPPORTED && !strategy) {
                // initialization failed for this strategy
                // try initializing the next one (unless a specific strategy was requested)
                this.selectNextUsableStrategy();
                if (this.strategy) {
                    return this.initializeSession();
                }
            }
            return result;
        });
        /**
         * Selects the given authentication method.
         */
        this.selectAuthenticationMethod = (method) => {
            const adapter = this.getAdapter(this.strategy);
            if (!adapter)
                return;
            const newIndex = adapter
                .authenticationMethods
                .findIndex(({ authenticationMethodType, authenticationCredentialReference }) => {
                return method.authenticationMethodType === authenticationMethodType &&
                    method.authenticationCredentialReference === authenticationCredentialReference;
            });
            if (newIndex !== -1) {
                adapter.selectedMethodIndex = newIndex;
            }
            this.authenticationStatus = adapter.selectedMethodIndex >= 0
                ? AuthenticationStatus.PENDING
                : AuthenticationStatus.NOT_SUPPORTED;
        };
        /**
         * Selects the next available authentication method.
         */
        this.selectNextUsableStrategy = () => {
            this.strategyIndex++;
        };
        /**
         * Initializes the user challenge (if applicable) for the current authentication method.
         */
        this.provideChallenge = (data) => this.invokeAdapterMethod('provideChallenge', data);
        /**
         * Attempts to validate the authentication session.
         */
        this.validateSession = () => this.invokeAdapterMethod('validateSession');
        /**
         * Binds the successfully-authenticated session to `appInstance`.
         *
         * @returns true if the session was successfully bound; false otherwise
         */
        this.bindSession = () => __awaiter(this, void 0, void 0, function* () {
            const authenticatedAdapter = Object
                .values(this.adapters)
                .find(({ isIdentityVerificationPerformed }) => isIdentityVerificationPerformed);
            return (authenticatedAdapter === null || authenticatedAdapter === void 0 ? void 0 : authenticatedAdapter.bindSession()) || false;
        });
        /**
         * Resets the current authentication session back to its original state.
         */
        this.clearSession = () => {
            var _a;
            this.authenticationStatus = AuthenticationStatus.PENDING;
            this.authenticationResult = AuthenticationResult.NOT_AUTHENTICATED;
            return ((_a = this
                .adapters[this.strategy]) === null || _a === void 0 ? void 0 : _a.clearSession()) || this.getSession();
        };
        /**
         * Returns whether or not the given strategy can be enrolled into.
         */
        this.isStrategyEnrollable = (strategy) => {
            var _a;
            return (_a = this.getAdapter(strategy)) === null || _a === void 0 ? void 0 : _a.isEnrollmentPending;
        };
        /**
        * Aggregates all KPI queues across adpaters and flushes to
        * /event/publish
        */
        this.flush = () => {
            this.kpi.flush(Object
                .values(this.adapters)
                .reduce(({ queue, remoteLogs }, { kpi }) => {
                const payload = {
                    queue: queue.concat(kpi.queue),
                    remoteLogs: remoteLogs.concat(kpi.remoteLogs)
                };
                kpi.queue = [];
                kpi.remoteLogs = [];
                return payload;
            }, {
                queue: this.kpi.queue,
                remoteLogs: this.kpi.remoteLogs
            }));
        };
        /**
         * Invokes the given method name on the active session adapter.
         */
        this.invokeAdapterMethod = (methodName, data) => __awaiter(this, void 0, void 0, function* () {
            if (this.authenticationResult === AuthenticationResult.AUTHENTICATED) {
                return this.getSession();
            }
            const session = yield (() => __awaiter(this, void 0, void 0, function* () {
                const adapter = this.adapters[this.strategy];
                try {
                    if ((adapter === null || adapter === void 0 ? void 0 : adapter.isUsable) && !isFatalStatus(adapter.authenticationStatus)) {
                        const result = yield adapter[methodName](data);
                        if (isFatalStatus(result.authenticationStatus)) {
                            adapter.kpi.end('FAILED');
                        }
                        else {
                            adapter.kpi.end('COMPLETED', adapter.onKpiSuccess());
                        }
                        return result;
                    }
                }
                catch (error) {
                    if (adapter) {
                        adapter.kpi.queueError(error);
                        adapter.kpi.end('FAILED');
                    }
                }
                return (adapter === null || adapter === void 0 ? void 0 : adapter.getSession()) || {
                    authenticationResult: AuthenticationResult.NOT_AUTHENTICATED,
                    authenticationStatus: AuthenticationStatus.NOT_SUPPORTED,
                    assuranceData: undefined
                };
            }))();
            return this.applySession(session);
        });
        /**
         * Returns the result of the current authentication session.
         */
        this.getSession = () => {
            var _a;
            return {
                authenticationResult: this.authenticationResult,
                authenticationStatus: this.authenticationStatus,
                assuranceData: this.assuranceData,
                methodAttributes: this.methodAttributes,
                error: (_a = this.getAdapter(this.strategy)) === null || _a === void 0 ? void 0 : _a.error
            };
        };
        /**
         * Applies the given adapter session result onto the state.
         */
        this.applySession = (session) => {
            const adapter = this.getAdapter(this.strategy);
            this.assuranceData = session.assuranceData;
            this.authenticationResult = session.authenticationResult;
            this.authenticationStatus = session.authenticationStatus;
            if (adapter === null || adapter === void 0 ? void 0 : adapter.isIdentityVerificationPerformed) {
                Object
                    .values(this.adapters)
                    .forEach(adapter => adapter.identityVerifications.push(session));
            }
            if (session.authenticationResult === AuthenticationResult.AUTHENTICATED) {
                this.cardToken = getCardToken(this.assuranceData, this.cardToken);
            }
            return this.getSession();
        };
        /**
         * Sets `isChallengeRequired` to `true` if at least one strategy can be enrolled into,
         * unless `suppressChallenge` is `true`.
         *
         * This should be called *after* strategies are bootstrapped for it to take effect.
         *
         * @param [force = true] - whether or not to force challenges, irrespective of whether
         *  or not a strategy can be enrolled into (unless `suppressChallenge` is `true`)
         */
        this.setChallengeRequirement = (force = true) => {
            if (this.suppressChallenge)
                return;
            const isChallengeRequired = !!Object
                .values(this.adapters)
                .find(({ isEnrollable }) => isEnrollable) || force;
            this.setAdapterStates('isChallengeRequired', isChallengeRequired);
        };
    }
}
//# sourceMappingURL=index.js.map